import { useState } from "react";
import { Link } from "@/lib/router";
import { usePathname } from "@/lib/router";
import { Inbox, ListChecks, Menu, Settings2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FloatingChat } from "@/components/floating-chat";
import { UploadNoteDialog } from "@/components/upload-note-dialog";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { href: "/", label: "설교 노트", icon: Inbox },
  { href: "/actions", label: "실천 기록", icon: ListChecks },
];

export type AppShellProps = {
  userEmail: string;
  children: React.ReactNode;
};

/**
 * (app-pages) 그룹 전체가 공유하는 사이드바+상단 토글바.
 *
 * 사이드바는 화면 폭에 따라 다르게 동작한다:
 * - 데스크톱(md 이상): 레이아웃 안에 있는 고정 폭 패널, 기본 열림
 *   (sidebarOpen).
 * - 모바일(md 미만): 콘텐츠를 밀어내지 않도록 흐름에서 빼서(fixed) 위에
 *   겹쳐 띄우는 오버레이, 기본 닫힘(mobileOpen). z-index만 올리면 순서만
 *   바뀌고 16rem 폭은 계속 차지하기 때문에 fixed로 흐름에서 빼야 한다.
 *
 * 두 상태를 따로 두는 이유: 서버 렌더 시점에는 화면 폭을 몰라서 "데스크톱은
 * 열림, 모바일은 닫힘"을 하나의 초기값으로 표현할 수 없다.
 */
export function AppShell({ userEmail, children }: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  function closeSidebar() {
    setSidebarOpen(false);
    setMobileOpen(false);
  }

  function openSidebar() {
    setSidebarOpen(true);
    setMobileOpen(true);
  }

  return (
    <div className="flex flex-1 bg-background text-foreground">
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-[45] bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      ) : null}

      <aside
        className={cn(
          "flex w-64 shrink-0 flex-col border-r border-border bg-sidebar p-4",
          // 데스크톱: 페이지가 길어져도 사이드바는 화면 높이로 고정(같이 스크롤되지 않게)
          "md:sticky md:top-0 md:h-dvh md:self-start md:overflow-y-auto",
          "max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:shadow-2xl",
          !mobileOpen && "max-md:hidden",
          !sidebarOpen && "md:hidden",
        )}
      >
        <div className="mb-4 flex items-center gap-2 px-1">
          <span className="text-sm font-semibold">Dabar Note</span>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto size-7"
            aria-label="사이드바 닫기"
            onClick={closeSidebar}
          >
            <X className="size-4" />
          </Button>
        </div>

        <UploadNoteDialog className="mb-4 justify-start gap-2" />

        <nav className="flex flex-col gap-1" aria-label="주 메뉴">
          <p className="px-2 pb-1 text-[11px] tracking-wide text-muted-foreground">
            WORKSPACE
          </p>
          {NAV_ITEMS.map((item) => {
            const active = pathname === item.href;
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2 py-2 text-sm",
                  active
                    ? "bg-accent font-medium text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <Icon className="size-4" /> {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto flex flex-col gap-1 border-t border-border pt-3">
          <Link
            href="/settings"
            onClick={() => setMobileOpen(false)}
            className={cn(
              "flex items-center gap-2 rounded-md px-2 py-2 text-sm",
              pathname === "/settings"
                ? "bg-accent font-medium text-accent-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
          >
            <Settings2 className="size-4" /> 설정
          </Link>
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="flex size-7 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
              {userEmail.slice(0, 1).toUpperCase()}
            </div>
            <span className="truncate text-xs text-muted-foreground">
              {userEmail}
            </span>
          </div>
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        {/* 모바일에서는 사이드바가 기본 닫힘이라 이 바가 항상 보이고,
            데스크톱에서는 사이드바가 닫혔을 때만 보인다 */}
        <div
          className={cn(
            "sticky top-0 z-30 flex h-14 shrink-0 items-center border-b border-border bg-background px-4",
            sidebarOpen && "md:hidden",
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="사이드바 열기"
            onClick={openSidebar}
          >
            <Menu className="size-4" />
          </Button>
        </div>

        {children}
      </section>

      <FloatingChat />
    </div>
  );
}
